import type {
  DecisionSource,
  EligibilityVerdict,
  ScopeSheetLoadError,
  ScopeSheetLoadRow,
} from "@/api/scope-activation";

import { NOTE_MAX_LENGTH } from "./aderencia/ScopeItemNoteEditor";
import {
  FORMULA_PREFIXES,
  SHEET_FIT_COLUMN,
  SHEET_ID_COLUMN,
  SHEET_NOTE_COLUMN,
  SHEET_NOTE_DATE_COLUMN,
  SHEET_SOURCE_COLUMN,
  SHEET_VERDICT_COLUMN,
} from "./scopeActivationFilter";

/** O maior CSV que o upload do Catálogo aceita. */
export const MAX_SHEET_BYTES = 2 * 1024 * 1024;

export type ParsedSheet =
  | { ok: true; rows: ScopeSheetLoadRow[]; hasVerdictColumn: boolean; hasNoteColumn: boolean }
  | { ok: false; errors: ScopeSheetLoadError[] };

/** O arquivo não está em UTF-8 nem em Windows-1252 legível. */
export class CsvEncodingError extends Error {
  constructor() {
    super("Codificação do CSV não reconhecida.");
  }
}

/**
 * O que a leitura em Windows-1252 mostra quando o arquivo não é Windows-1252:
 * - UTF-8 mal lido nas letras: "ç" vira "Ã§", "Ç" vira "Ã‡", "Ã" vira "Ãƒ" (o
 *   "Ã" ou "Â" antes de um caractere de 0x80–0xBF, ou do que o Windows-1252 dá
 *   aos bytes 0x80–0x9F);
 * - UTF-8 mal lido na pontuação tipográfica: "–" vira "â€“", "“" vira "â€œ",
 *   "€" vira "â‚¬";
 * - controle C1: bytes que o Windows-1252 não define (0x81, 0x8D, 0x8F, 0x90,
 *   0x9D), como o "ç" do Mac Roman do Excel para Mac.
 */
const MOJIBAKE =
  /[ÃÂ][\u0080-¿€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ]|â€|â‚|[\u0080-\u009F]/;

/**
 * Texto do arquivo. Com BOM UTF-8, só UTF-8 estrito. Sem BOM, UTF-8 primeiro (o
 * download e o Google Sheets) e Windows-1252 de reserva (o "CSV (separado por
 * vírgulas)" do Excel no Windows), recusando o que nesse caminho mostra UTF-8
 * mal lido. UTF-16 é recusado.
 */
export function decodeCsvBytes(bytes: Uint8Array): string {
  const utf16 =
    (bytes[0] === 0xff && bytes[1] === 0xfe) || (bytes[0] === 0xfe && bytes[1] === 0xff);
  if (utf16) throw new CsvEncodingError();
  const strict = new TextDecoder("utf-8", { fatal: true });
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    try {
      return strict.decode(bytes.subarray(3));
    } catch {
      throw new CsvEncodingError();
    }
  }
  try {
    return strict.decode(bytes);
  } catch {
    const text = new TextDecoder("windows-1252").decode(bytes);
    if (MOJIBAKE.test(text)) throw new CsvEncodingError();
    return text;
  }
}

/** `;` do Excel pt-BR ou `,` do Google Sheets, contados no cabeçalho. */
export function detectDelimiter(text: string): ";" | "," {
  let quoted = false;
  let semicolons = 0;
  let commas = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === '"') {
      if (quoted && text[i + 1] === '"') i++;
      else quoted = !quoted;
    } else if (!quoted) {
      if (char === "\r" || char === "\n") break;
      if (char === ";") semicolons++;
      if (char === ",") commas++;
    }
  }
  return commas > semicolons ? "," : ";";
}

class CsvStructureError extends Error {
  constructor(readonly line: number) {
    super("Aspas não fechadas no CSV.");
  }
}

/**
 * CSV no estilo RFC 4180, tolerante: aspas guardam delimitador, aspas dobradas e
 * quebra de linha; aceita `\r` sozinho e texto depois das aspas fechadas.
 */
export function parseCsv(text: string, delimiter: ";" | "," = detectDelimiter(text)): string[][] {
  const records: string[][] = [];
  let record: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c !== '"') {
        cell += c;
      } else if (text[i + 1] === '"') {
        cell += '"';
        i++;
      } else {
        quoted = false;
      }
    } else if (c === '"' && cell === "") {
      quoted = true;
    } else if (c === delimiter) {
      record.push(cell);
      cell = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      record.push(cell);
      records.push(record);
      record = [];
      cell = "";
    } else {
      cell += c;
    }
  }
  if (quoted) throw new CsvStructureError(records.length + 1);
  if (cell !== "" || record.length > 0) {
    record.push(cell);
    records.push(record);
  }
  return records;
}

// O token e o rótulo da tela nos três idiomas da interface (en-US, pt-BR, es-ES).
const VERDICT_ALIASES: Record<string, EligibilityVerdict> = {
  activate: "activate",
  ativar: "activate",
  activar: "activate",
  decision: "decision",
  "decisão": "decision",
  decisao: "decision",
  "decisión": "decision",
  out_of_scope: "out_of_scope",
  "out of scope": "out_of_scope",
  "fora de escopo": "out_of_scope",
  "fuera de alcance": "out_of_scope",
};

/**
 * Desfaz o apóstrofo que o download (`esc`, em `scopeActivationFilter.ts`) põe na
 * frente de célula com cara de fórmula ou que já começa com apóstrofo.
 */
function unguard(value: string): string {
  const guarded =
    value.startsWith("'") &&
    (value.startsWith("''") || FORMULA_PREFIXES.some((prefix) => value.slice(1).startsWith(prefix)));
  return guarded ? value.slice(1) : value;
}

const hasContent = (record: string[]) => record.some((cell) => cell.trim() !== "");

/**
 * Linhas da carga a partir da matriz do CSV, achando as colunas pelo nome.
 *
 * `line` é a linha da planilha (cabeçalho = 1), a que o consultor procura; uma
 * nota com quebra de linha continua sendo uma linha só. Linha sem conteúdo é
 * pulada. Veredito vazio vira `null` (não mexe); nota vazia vai como `""`
 * (remove a nota); coluna ausente não manda o campo.
 */
export function sheetRowsFromMatrix(matrix: string[][]): ParsedSheet {
  if (matrix.length === 0 || !hasContent(matrix[0])) {
    return { ok: false, errors: [{ line: null, code: "empty_file" }] };
  }
  const rows: ScopeSheetLoadRow[] = [];
  const errors: ScopeSheetLoadError[] = [];
  const header = matrix[0].map((name) => name.trim().toLowerCase());
  for (const name of [SHEET_ID_COLUMN, SHEET_VERDICT_COLUMN, SHEET_NOTE_COLUMN]) {
    if (header.filter((column) => column === name).length > 1) {
      errors.push({ line: 1, code: "duplicate_header", value: name });
    }
  }
  if (errors.length > 0) return { ok: false, errors };
  // O export do Scope Activation tem o mesmo nome de arquivo e uma coluna
  // `verdict` com o fit do ERP; sem `fit_erp`, com a evidência e sem `note`, é
  // ele. O `note` separa o do Catálogo em que o consultor apagou o `fit_erp`.
  const foreign =
    header.includes(SHEET_VERDICT_COLUMN) &&
    !header.includes(SHEET_FIT_COLUMN) &&
    !header.includes(SHEET_NOTE_COLUMN) &&
    (header.includes("triggered_by") || header.includes("capabilities"));
  if (foreign) return { ok: false, errors: [{ line: 1, code: "foreign_export" }] };
  const idCol = header.indexOf(SHEET_ID_COLUMN);
  const verdictCol = header.indexOf(SHEET_VERDICT_COLUMN);
  const noteCol = header.indexOf(SHEET_NOTE_COLUMN);
  if (idCol < 0) return { ok: false, errors: [{ line: 1, code: "missing_id_column" }] };
  if (verdictCol < 0 && noteCol < 0) {
    return { ok: false, errors: [{ line: 1, code: "no_editable_column" }] };
  }

  const seen = new Set<string>();
  matrix.slice(1).forEach((record, index) => {
    const line = index + 2;
    if (!hasContent(record)) return;
    const id = (record[idCol] ?? "").trim();
    if (!id) {
      errors.push({ line, code: "missing_id" });
      return;
    }
    if (seen.has(id)) {
      errors.push({ line, scope_item_id: id, code: "duplicate_scope_item" });
      return;
    }
    seen.add(id);
    if ([verdictCol, noteCol].some((column) => column >= record.length)) {
      errors.push({ line, scope_item_id: id, code: "missing_editable_cell" });
      return;
    }

    const row: ScopeSheetLoadRow = { line, scope_item_id: id };
    if (verdictCol >= 0) {
      const raw = unguard((record[verdictCol] ?? "").trim());
      if (!raw) {
        row.verdict = null;
      } else {
        const alias = raw.toLowerCase();
        const verdict = Object.prototype.hasOwnProperty.call(VERDICT_ALIASES, alias)
          ? VERDICT_ALIASES[alias]
          : undefined;
        if (!verdict) {
          errors.push({ line, scope_item_id: id, code: "unknown_verdict", value: raw });
          return;
        }
        row.verdict = verdict;
      }
    }
    if (noteCol >= 0) {
      const note = unguard(record[noteCol] ?? "").trim();
      if (note.length > NOTE_MAX_LENGTH) {
        errors.push({ line, scope_item_id: id, code: "note_too_long" });
        return;
      }
      row.note = note;
    }
    rows.push(row);
  });
  return errors.length > 0
    ? { ok: false, errors }
    : { ok: true, rows, hasVerdictColumn: verdictCol >= 0, hasNoteColumn: noteCol >= 0 };
}

// --- Colunas que a carga não lê ----------------------------------------------
// O download traz colunas de consulta ao lado das duas editáveis. O que se
// escreve nelas não é gravado; sem o aviso, a carga só diz "sem mudança".

/** Uma coluna do arquivo que a carga ignorou, com a letra da planilha. */
export interface IgnoredColumnEdit {
  /** O nome do cabeçalho como está no arquivo; vazio na coluna sem nome. */
  column: string;
  letter: string;
  rows: number;
}

const READ_COLUMNS = new Set([SHEET_ID_COLUMN, SHEET_VERDICT_COLUMN, SHEET_NOTE_COLUMN]);

/** Exaustivo pelo tipo: origem nova não compila sem entrar aqui. */
const DECISION_SOURCES: Record<DecisionSource, true> = {
  analyst: true,
  suggestion_accepted: true,
  excel_load: true,
};

/**
 * Data como o download escreve (ISO) ou como a planilha a regrava
 * (`29/10/2026 14:56`, ou o número de série do Excel).
 */
const DATE_LIKE = /^(\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4}|\d+(?:[.,]\d+)?$)/;

/**
 * Colunas que a curadoria muda depois do download: comparadas com a plataforma,
 * acusariam todo arquivo baixado antes da última carga. Nelas só conta o que o
 * download nunca escreveria.
 */
const CURATION_COLUMNS = new Map<string, (value: string) => boolean>([
  [SHEET_SOURCE_COLUMN, (value) => Object.prototype.hasOwnProperty.call(DECISION_SOURCES, value)],
  [SHEET_NOTE_DATE_COLUMN, (value) => DATE_LIKE.test(value)],
]);

/** A letra da coluna na planilha: 0 → A, 25 → Z, 26 → AA. */
function columnLetter(index: number): string {
  let letter = "";
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    letter = String.fromCharCode(65 + ((n - 1) % 26)) + letter;
  }
  return letter;
}

/**
 * As colunas do arquivo que a carga não lê e em que ele difere da plataforma, na
 * ordem da planilha, com quantas linhas.
 *
 * `platform` é a matriz que o download escreveria agora (`scopeItemRowsToMatrix`).
 * Célula vazia não conta: apagar o que é só consulta não perde nada. Coluna que o
 * download não tem conta sempre que tem conteúdo; as de curadoria, pelo formato;
 * as outras, pela diferença com a plataforma, nas linhas cujo id ela reconhece.
 */
export function ignoredColumnEdits(file: string[][], platform: string[][]): IgnoredColumnEdit[] {
  if (file.length === 0 || platform.length === 0) return [];
  const header = file[0].map((name) => name.trim().toLowerCase());
  const idCol = header.indexOf(SHEET_ID_COLUMN);
  const platformCols = header.map((name) => platform[0].indexOf(name));
  const platformIdCol = platform[0].indexOf(SHEET_ID_COLUMN);
  const platformById = new Map(platform.slice(1).map((record) => [record[platformIdCol], record]));

  const counts = new Map<number, number>();
  for (const record of file.slice(1)) {
    const current = platformById.get((record[idCol] ?? "").trim());
    header.forEach((name, column) => {
      if (READ_COLUMNS.has(name)) return;
      const value = unguard((record[column] ?? "").trim());
      if (!value) return;
      const shape = CURATION_COLUMNS.get(name);
      const edited =
        platformCols[column] < 0
          ? true
          : shape
            ? !shape(value)
            : current !== undefined && value !== current[platformCols[column]].trim();
      if (edited) counts.set(column, (counts.get(column) ?? 0) + 1);
    });
  }
  return [...counts]
    .sort(([a], [b]) => a - b)
    .map(([column, rows]) => ({ column: file[0][column].trim(), letter: columnLetter(column), rows }));
}

/** O Blob do jsdom não tem `arrayBuffer()`; o FileReader funciona nos dois. */
function readBytes(file: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

/** O arquivo lido: as linhas da carga e, para o aviso das colunas ignoradas, a matriz. */
export type ReadSheet =
  | (Extract<ParsedSheet, { ok: true }> & { matrix: string[][] })
  | Extract<ParsedSheet, { ok: false }>;

/** Lê o arquivo escolhido no input até as linhas da carga. */
export async function readSheetFile(file: File): Promise<ReadSheet> {
  if (file.size > MAX_SHEET_BYTES) {
    return { ok: false, errors: [{ line: null, code: "too_large" }] };
  }
  const bytes = await readBytes(file);
  try {
    const matrix = parseCsv(decodeCsvBytes(bytes));
    const parsed = sheetRowsFromMatrix(matrix);
    return parsed.ok ? { ...parsed, matrix } : parsed;
  } catch (error) {
    if (error instanceof CsvEncodingError) {
      return { ok: false, errors: [{ line: null, code: "invalid_encoding" }] };
    }
    if (error instanceof CsvStructureError) {
      return { ok: false, errors: [{ line: error.line, code: "unclosed_quote" }] };
    }
    throw error;
  }
}
